import { useState, useEffect, useCallback } from 'react';
import { getExpiringPhotoDiagnoses, getDaysUntilExpiration } from '../services/diagnosis';
import { extendPhotoRetention } from '../services/api';
import type { Diagnosis } from '../types/diagnosis';

interface ExpiringPhoto {
  plantId: string;
  diagnosis: Diagnosis;
  daysRemaining: number;
}

interface UsePhotoExpirationResult {
  expiringPhotos: ExpiringPhoto[];
  loading: boolean;
  error: string | null;
  isDismissed: boolean;
  dismiss: () => void;
  extendAll: () => Promise<void>;
  extendOne: (plantId: string, diagnosisId: string) => Promise<void>;
  extending: boolean;
}

/**
 * Hook to manage photo expiration warnings.
 * Checks for diagnoses with photos expiring within 7 days.
 * Dismiss state only persists for the current session.
 */
export function usePhotoExpiration(userId: string | null): UsePhotoExpirationResult {
  const [expiringPhotos, setExpiringPhotos] = useState<ExpiringPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [extending, setExtending] = useState(false);

  // Fetch expiring photos on mount and when userId changes
  useEffect(() => {
    if (!userId) {
      setExpiringPhotos([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    getExpiringPhotoDiagnoses(userId)
      .then((results) => {
        if (mounted) {
          const withDays = results.map(({ plantId, diagnosis }) => ({
            plantId,
            diagnosis,
            daysRemaining: getDaysUntilExpiration(diagnosis.photoExpiresAt),
          }));
          setExpiringPhotos(withDays);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error('Failed to fetch expiring photos:', err);
          setError('Failed to check for expiring photos');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const extendOne = useCallback(
    async (plantId: string, diagnosisId: string) => {
      setExtending(true);
      try {
        await extendPhotoRetention({ plantId, diagnosisId });
        // Remove from expiring list after successful extension
        setExpiringPhotos((prev) =>
          prev.filter((ep) => !(ep.plantId === plantId && ep.diagnosis.id === diagnosisId))
        );
      } catch (err) {
        console.error('Failed to extend photo retention:', err);
        throw err;
      } finally {
        setExtending(false);
      }
    },
    []
  );

  const extendAll = useCallback(async () => {
    setExtending(true);
    try {
      // Extend all photos in parallel
      await Promise.all(
        expiringPhotos.map((ep) =>
          extendPhotoRetention({ plantId: ep.plantId, diagnosisId: ep.diagnosis.id })
        )
      );
      // Clear the list after all extensions succeed
      setExpiringPhotos([]);
    } catch (err) {
      console.error('Failed to extend some photo retentions:', err);
      // Refresh the list to show remaining expiring photos
      if (userId) {
        const results = await getExpiringPhotoDiagnoses(userId);
        const withDays = results.map(({ plantId, diagnosis }) => ({
          plantId,
          diagnosis,
          daysRemaining: getDaysUntilExpiration(diagnosis.photoExpiresAt),
        }));
        setExpiringPhotos(withDays);
      }
      throw err;
    } finally {
      setExtending(false);
    }
  }, [expiringPhotos, userId]);

  return {
    expiringPhotos,
    loading,
    error,
    isDismissed,
    dismiss,
    extendAll,
    extendOne,
    extending,
  };
}
